// src/modules/hr/pages/PayrollPreparation.jsx
// Phase 22 (attendance prep/export) + Phase 22.5 (payroll runs, payslips, PDF).
import React, { useEffect, useState } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Select, DatePicker, message, Space, Tag, Tabs, Modal, Popconfirm, Statistic, Alert, InputNumber, Input, Upload, Radio, Divider, Spin, Dropdown, Tooltip,
} from 'antd';
import { DollarOutlined, DownloadOutlined, ReloadOutlined, FileTextOutlined, PlusOutlined, CheckOutlined, SaveOutlined, UploadOutlined, GiftOutlined, SettingOutlined, DeleteOutlined, CalendarOutlined, CloudUploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import API from '../../../services/api';
import { fetchBranches } from '../../branches/api';
import {
  fetchPayrollSummary, generatePayrollRun, fetchPayrollRuns, fetchPayrollRun,
  finalizePayrollRun, markPayrollRunPaid, cancelPayrollRun, payslipPdfUrl,
  publishPayrollRun, publishPayslip,
  fetchEmployees, fetchIncentives, saveIncentive, importIncentives,
  fetchPTaxSettings, updatePTaxSettings,
  fetchPayrollCycle, updatePayrollCycle, previewPayrollCycle,
} from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

dayjs.extend(utcPlugin);

const { Title, Text } = Typography;
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const periodLabel = (r) => dayjs(new Date(r.year, r.month - 1, 1)).format('MMM YYYY');
// Business dates come back as UTC-midnight of the local calendar date — read them
// in UTC so the displayed day never slips backwards.
const bizDate = (d) => (d ? dayjs(d).utc().format('DD MMM YYYY') : '—');
// The days a run/slip actually covers, e.g. "26 Jul 2026 – 25 Aug 2026".
const periodRange = (r) => (r?.periodStart && r?.periodEnd ? `${bizDate(r.periodStart)} – ${bizDate(r.periodEnd)}` : '—');

const RUN_STATUS_COLOR = { DRAFT: 'default', FINALIZED: 'blue', PAID: 'success', CANCELLED: 'error' };
const SLIP_STATUS_COLOR = { DRAFT: 'default', FINALIZED: 'blue', PAID: 'success', CANCELLED: 'error' };
// Whether a payslip's PDF has been archived into the employee's folder.
const PUBLISH_TAG = {
  PUBLISHED: { color: 'success', label: 'Archived' },
  FAILED: { color: 'error', label: 'Failed' },
  NOT_PUBLISHED: { color: 'default', label: 'Not archived' },
};

// ── CSV export ────────────────────────────────────────────────────────────────
// A leading =, +, - or @ makes Excel and Sheets treat a cell as a formula, so a
// name like "-Ipsita" would execute rather than display. Prefix those with a
// single quote, which spreadsheets strip on render.
const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  let s = String(v);
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// `columns` is [label, accessor] pairs — the header row and the value per slip.
const toCsv = (rows, columns) => [
  columns.map(([label]) => csvCell(label)).join(','),
  ...rows.map((r) => columns.map(([, get]) => csvCell(get(r))).join(',')),
].join('\n');

function downloadCsv(filename, csv) {
  // The BOM makes Excel read the ₹ sign and non-ASCII names as UTF-8.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

// Amounts export as bare numbers, never "₹1,234.00" — a formatted string is not
// summable in a spreadsheet, which is the whole point of exporting.
const num = (v) => (v === null || v === undefined || v === '' ? '' : Number(v));
const csvDate = (d) => (d ? dayjs(d).utc().format('YYYY-MM-DD') : '');

// Exactly the three columns asked for — the disbursement list.
const SUMMARY_COLUMNS = [
  ['Ref', (s) => s.reference],
  ['Employee', (s) => s.employeeName],
  ['Net Pay', (s) => num(s.netPay)],
];

// Everything the payslip holds: who, which days, the attendance it was computed
// from, and every earning and deduction line separately.
const FULL_COLUMNS = [
  ['Ref', (s) => s.reference],
  ['Employee Code', (s) => s.employeeCode],
  ['Employee', (s) => s.employeeName],
  ['Designation', (s) => s.designation],
  ['Department', (s) => s.department],
  ['Branch', (s) => s.branchName || s.branch?.name],
  ['Period Start', (s) => csvDate(s.periodStart)],
  ['Period End', (s) => csvDate(s.periodEnd)],
  ['Pay Date', (s) => csvDate(s.payDate)],
  ['Cycle', (s) => s.cycle?.description],
  ['Total Days', (s) => num(s.attendance?.totalDays)],
  ['Present', (s) => num(s.attendance?.present)],
  ['Absent', (s) => num(s.attendance?.absent)],
  ['Half Day', (s) => num(s.attendance?.halfDay)],
  ['Leave', (s) => num(s.attendance?.leave)],
  ['Holiday', (s) => num(s.attendance?.holiday)],
  ['Weekly Off', (s) => num(s.attendance?.weeklyOff)],
  ['LWP', (s) => num(s.attendance?.lwp)],
  ['Late Marks', (s) => num(s.attendance?.lateMarks)],
  ['Overtime Hours', (s) => num(s.attendance?.overtimeHours)],
  ['Payable Days', (s) => num(s.attendance?.payableDays)],
  ['LOP Days', (s) => num(s.attendance?.lopDays)],
  ['Prorated Over (days)', (s) => num(s.attendance?.prorationDays)],
  ['Structure Gross', (s) => num(Object.values(s.structure?.earnings || {}).reduce((a, b) => a + (Number(b) || 0), 0))],
  ['Basic', (s) => num(s.earnings?.basic)],
  ['HRA', (s) => num(s.earnings?.hra)],
  ['Conveyance', (s) => num(s.earnings?.conveyance)],
  ['Medical Allowance', (s) => num(s.earnings?.medicalAllowance)],
  ['Special Allowance', (s) => num(s.earnings?.specialAllowance)],
  ['Other Allowances', (s) => num(s.earnings?.otherAllowances)],
  ['Overtime Pay', (s) => num(s.earnings?.overtimePay)],
  ['Incentive', (s) => num(s.earnings?.incentive)],
  ['Gross Earnings', (s) => num(s.grossEarnings)],
  ['PF', (s) => num(s.deductions?.pf)],
  ['ESI', (s) => num(s.deductions?.esi)],
  ['Professional Tax', (s) => num(s.deductions?.professionalTax)],
  ['TDS', (s) => num(s.deductions?.tds)],
  ['Other Deductions', (s) => num(s.deductions?.otherDeductions)],
  ['Total Deductions', (s) => num(s.totalDeductions)],
  ['Net Pay', (s) => num(s.netPay)],
  ['Status', (s) => s.status],
  ['PDF Archived', (s) => s.publishStatus],
  ['Archived PDF URL', (s) => s.pdfUrl],
];

async function openPayslipPdf(id) {
  try {
    const res = await API.get(payslipPdfUrl(id), { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (err) {
    message.error(err?.response?.data?.message || 'Could not open payslip');
  }
}

// ── Tab 1: attendance preparation summary (existing behaviour) ──
function PreparationTab({ branches }) {
  const [branchId, setBranchId] = useState(null);
  const [period, setPeriod] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [covered, setCovered] = useState(null);

  const params = () => ({ year: period.year(), month: period.month() + 1, ...(branchId ? { branchId } : {}) });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchPayrollSummary(params());
      const data = res?.data?.data || {};
      setRows(data.rows || []);
      // The prep sheet counts the payroll cycle's days, not the calendar month.
      setCovered(data.period || null);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await API.get('/hr/payroll/export', { params: params(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `payroll-prep-${period.year()}-${period.month() + 1}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { message.error(err?.response?.data?.message || 'Export failed'); }
  };

  const columns = [
    { title: 'Code', dataIndex: 'employeeCode', width: 100, render: (v) => v ? <Text code>{v}</Text> : '—' },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Branch', dataIndex: 'branch', render: (v) => v || '—' },
    { title: 'Present', dataIndex: 'present', align: 'right', width: 80 },
    { title: 'Absent', dataIndex: 'absent', align: 'right', width: 80 },
    { title: 'Half', dataIndex: 'halfDay', align: 'right', width: 70 },
    { title: 'Leave', dataIndex: 'leave', align: 'right', width: 70 },
    { title: 'Holiday', dataIndex: 'holiday', align: 'right', width: 80 },
    { title: 'W-Off', dataIndex: 'weeklyOff', align: 'right', width: 70 },
    { title: 'Late', dataIndex: 'lateMarks', align: 'right', width: 70 },
    { title: 'OT', dataIndex: 'overtimeHours', align: 'right', width: 70 },
    { title: 'LWP', dataIndex: 'lwp', align: 'right', width: 70, render: (v) => v ? <Tag color="red">{v}</Tag> : 0 },
    { title: 'Payable', dataIndex: 'payableDays', align: 'right', width: 90, render: (v) => <Text strong>{v}</Text> },
  ];

  return (
    <>
      <Row justify="end" align="middle" style={{ marginBottom: 12 }}>
        <Space>
          <Select allowClear showSearch optionFilterProp="children" style={{ width: 200 }} placeholder="All branches" value={branchId} onChange={(v) => setBranchId(v || null)}>{branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}</Select>
          <DatePicker picker="month" value={period} onChange={(d) => setPeriod(d || dayjs())} />
          <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>Generate</Button>
          <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>
        </Space>
      </Row>
      {covered && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={<>Counting <Text strong>{periodRange(covered)}</Text> — the payroll cycle for {periodLabel({ year: period.year(), month: period.month() + 1 })} ({covered.totalDays} days).</>}
        />
      )}
      <Table rowKey="employeeId" columns={columns} dataSource={rows} loading={loading} size="small" scroll={{ x: 1200 }}
        pagination={{ pageSize: 50, showTotal: (t) => `${t} employees` }} locale={{ emptyText: 'Generate a summary to see results' }} />
    </>
  );
}

// ── Payroll cycle — the window of days each run pays for ──
// The run is still LABELLED by a month; this setting decides which days that
// label covers (e.g. August payroll = 26 Jul → 25 Aug) and the denominator
// salary is prorated over.
const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const START_DAY_OPTIONS = [
  { value: 1, label: '1st — plain calendar month' },
  ...Array.from({ length: 27 }, (_, i) => {
    const d = i + 2;
    return { value: d, label: `${ordinal(d)} — cycle runs ${ordinal(d)} to ${ordinal(d - 1)}` };
  }),
];

const DEFAULT_CYCLE = { startDay: 1, labelMonth: 'END', prorationBasis: 'ACTUAL', fixedDays: 30, payDayOffset: 0 };

// One-line rendering of the cycle that is actually stored, used to contrast the
// saved settings against an edited-but-unsaved preview.
const describeSavedCycle = (c) => {
  const window = c.startDay === 1
    ? 'calendar month'
    : `${ordinal(c.startDay)} to ${ordinal(c.startDay - 1)}`;
  const divisor = c.prorationBasis === 'FIXED_30' ? 'gross ÷ 30'
    : c.prorationBasis === 'FIXED_DAYS' ? `gross ÷ ${c.fixedDays}`
      : 'gross ÷ days in the cycle';
  return `${window}, ${divisor}`;
};

function PayrollCycleButton({ onSaved }) {
  const { can } = usePermissions();
  const canEdit = can('hrPayroll', 'edit');

  const [open, setOpen] = useState(false);
  const [cycle, setCycle] = useState(DEFAULT_CYCLE);
  // What is actually stored on the server. The preview below re-resolves on
  // every keystroke, so without this the modal happily shows a cycle nobody
  // saved — read as fact, that is how a run gets blamed for dates it never used.
  const [savedCycle, setSavedCycle] = useState(DEFAULT_CYCLE);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(cycle) !== JSON.stringify(savedCycle);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchPayrollCycle();
      const d = res?.data?.data || {};
      const next = { ...DEFAULT_CYCLE, ...(d.cycle || {}) };
      setCycle(next);
      setSavedCycle(next);
      setPreview(d.preview || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load payroll cycle'); }
    finally { setLoading(false); }
  };

  const openModal = () => { setOpen(true); load(); };

  // Every edit re-resolves the real dates on the server, so what the preview
  // shows is exactly what a run would use — no duplicated date math here.
  const change = async (patch) => {
    const next = { ...cycle, ...patch };
    setCycle(next);
    try {
      const res = await previewPayrollCycle(next);
      setPreview(res?.data?.data?.preview || []);
    } catch { /* preview is advisory; a failure must not block editing */ }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updatePayrollCycle(cycle);
      const d = res?.data?.data || {};
      message.success(res?.data?.message || 'Payroll cycle saved');
      const next = { ...DEFAULT_CYCLE, ...(d.cycle || {}) };
      setCycle(next);
      setSavedCycle(next);
      setPreview(d.preview || []);
      if (onSaved) onSaved(d);
    } catch (err) { message.error(err?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const previewColumns = [
    { title: 'Payroll for', render: (_, r) => <Text strong>{dayjs(new Date(r.year, r.month - 1, 1)).format('MMM YYYY')}</Text> },
    { title: 'Covers', render: (_, r) => periodRange(r) },
    { title: 'Days', dataIndex: 'totalDays', align: 'right', width: 70 },
    { title: 'Prorated over', dataIndex: 'prorationDays', align: 'right', width: 120, render: (v) => `${v} days` },
    { title: 'Pay date', render: (_, r) => bizDate(r.payDate) },
  ];

  const row = (label, hint, control) => (
    <Row gutter={12} align="middle" style={{ marginBottom: 14 }}>
      <Col span={9}>
        <Text strong>{label}</Text>
        {hint && <div><Text type="secondary" style={{ fontSize: 12 }}>{hint}</Text></div>}
      </Col>
      <Col span={15}>{control}</Col>
    </Row>
  );

  return (
    <>
      <Button icon={<CalendarOutlined />} onClick={openModal}>Payroll Cycle</Button>
      <Modal open={open} onCancel={() => setOpen(false)} width={760} destroyOnClose
        title={<><CalendarOutlined style={{ marginRight: 8 }} />Payroll Cycle</>}
        footer={canEdit ? [
          <Button key="close" onClick={() => setOpen(false)}>Close</Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Save Cycle</Button>,
        ] : [<Button key="close" onClick={() => setOpen(false)}>Close</Button>]}>
        <Spin spinning={loading}>
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="This decides which days a payroll run actually pays for. Runs are still named by month — an August run on a 26th-start cycle covers 26 Jul to 25 Aug. Saving affects draft and future runs only; finalized runs keep the cycle they were generated on."
          />

          {row('Cycle starts on', 'Days 29-31 are not offered — February cannot supply them.',
            <Select style={{ width: '100%' }} value={cycle.startDay} disabled={!canEdit}
              options={START_DAY_OPTIONS} onChange={(v) => change({ startDay: v })} />)}

          {cycle.startDay > 1 && row('Period is named after', 'Which calendar month gives a straddling cycle its name.',
            <Radio.Group value={cycle.labelMonth} disabled={!canEdit} onChange={(e) => change({ labelMonth: e.target.value })}>
              <Radio.Button value="END">The month it ends in</Radio.Button>
              <Radio.Button value="START">The month it starts in</Radio.Button>
            </Radio.Group>)}

          {row('Per-day wage divisor', 'The N in "one lost day costs gross ÷ N". Days in the cycle: N moves with the period, so a lost day is worth more in a short month. Fixed 30, or a number you type: N never moves. Either way, full attendance pays exactly the salary structure.',
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Radio.Group value={cycle.prorationBasis} disabled={!canEdit} onChange={(e) => change({ prorationBasis: e.target.value })}>
                <Radio.Button value="ACTUAL">Days in the cycle</Radio.Button>
                <Radio.Button value="FIXED_30">Fixed 30 days</Radio.Button>
                <Radio.Button value="FIXED_DAYS">Fixed number of days</Radio.Button>
              </Radio.Group>
              {cycle.prorationBasis === 'FIXED_DAYS' && (
                <Space>
                  <Text type="secondary">Divide gross by</Text>
                  <InputNumber min={1} max={31} style={{ width: 90 }} value={cycle.fixedDays} disabled={!canEdit}
                    onChange={(v) => change({ fixedDays: v == null ? 30 : v })} />
                  <Text type="secondary">days</Text>
                </Space>
              )}
            </Space>)}

          {row('Salary paid', 'Days after the cycle closes.',
            <Space>
              <InputNumber min={0} max={31} style={{ width: 90 }} value={cycle.payDayOffset} disabled={!canEdit}
                onChange={(v) => change({ payDayOffset: v == null ? 0 : v })} />
              <Text type="secondary">day(s) after the cycle ends</Text>
            </Space>)}

          <Divider style={{ margin: '8px 0 14px' }}>
            {dirty ? <Tag color="warning">Unsaved preview</Tag> : 'What this means'}
          </Divider>
          {dirty && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message="These dates are a preview of settings you have not saved yet."
              description={<>Runs still use the saved cycle — <Text strong>{describeSavedCycle(savedCycle)}</Text> — until you press Save Cycle.</>}
            />
          )}
          <Table rowKey={(r) => `${r.year}-${r.month}`} columns={previewColumns} dataSource={preview}
            size="small" pagination={false} />
        </Spin>
      </Modal>
    </>
  );
}

// ── Professional Tax slabs — global config used at payroll time ──
// A payslip's professional tax is picked from the slab whose [from, to] window
// contains that month's gross earnings, replacing the employee's structure value.
function PTaxSettingsButton() {
  const { can } = usePermissions();
  const canEdit = can('hrPayroll', 'edit');

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const toRows = (slabs) => (slabs || []).map((s, i) => ({
    key: `${i}-${Date.now()}`,
    from: s.from ?? 0,
    to: s.to === null || s.to === undefined ? '' : s.to,
    amount: s.amount ?? 0,
  }));

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchPTaxSettings();
      setRows(toRows(res?.data?.data?.slabs));
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load P-Tax settings'); }
    finally { setLoading(false); }
  };

  const openModal = () => { setOpen(true); load(); };

  const addRow = () => setRows((r) => [...r, { key: `new-${Date.now()}`, from: r.length ? '' : 0, to: '', amount: '' }]);
  const removeRow = (key) => setRows((r) => r.filter((x) => x.key !== key));
  const setCell = (key, field, val) => setRows((r) => r.map((x) => (x.key === key ? { ...x, [field]: val } : x)));

  const save = async () => {
    setSaving(true);
    try {
      const slabs = rows.map((r) => ({
        from: r.from === '' || r.from == null ? 0 : Number(r.from),
        to: r.to === '' || r.to == null ? null : Number(r.to),
        amount: r.amount === '' || r.amount == null ? 0 : Number(r.amount),
      }));
      const res = await updatePTaxSettings({ slabs });
      message.success(res?.data?.message || 'Saved');
      setRows(toRows(res?.data?.data?.slabs));
    } catch (err) { message.error(err?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const columns = [
    {
      title: 'From (₹)', width: 160,
      render: (_, r) => (
        <InputNumber min={0} style={{ width: '100%' }} value={r.from} disabled={!canEdit}
          formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(v) => (v || '').replace(/,/g, '')}
          onChange={(v) => setCell(r.key, 'from', v)} />
      ),
    },
    {
      title: 'Up to (₹)', width: 180,
      render: (_, r) => (
        <InputNumber min={0} style={{ width: '100%' }} value={r.to} disabled={!canEdit} placeholder="Blank = & above"
          formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(v) => (v || '').replace(/,/g, '')}
          onChange={(v) => setCell(r.key, 'to', v)} />
      ),
    },
    {
      title: 'P-Tax / month (₹)', width: 160,
      render: (_, r) => (
        <InputNumber min={0} style={{ width: '100%' }} value={r.amount} disabled={!canEdit}
          onChange={(v) => setCell(r.key, 'amount', v)} />
      ),
    },
    ...(canEdit ? [{
      title: '', width: 50,
      render: (_, r) => <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => removeRow(r.key)} />,
    }] : []),
  ];

  return (
    <>
      <Button icon={<SettingOutlined />} onClick={openModal}>P-Tax Slabs</Button>
      <Modal open={open} onCancel={() => setOpen(false)} width={640} destroyOnClose
        title={<><SettingOutlined style={{ marginRight: 8 }} />Professional Tax Slabs</>}
        footer={canEdit ? [
          <Button key="add" icon={<PlusOutlined />} onClick={addRow} style={{ float: 'left' }}>Add Row</Button>,
          <Button key="close" onClick={() => setOpen(false)}>Close</Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Save Slabs</Button>,
        ] : [<Button key="close" onClick={() => setOpen(false)}>Close</Button>]}>
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="When a payroll run is generated, each employee's Professional Tax is picked from the slab whose range contains that month's gross earnings — the value on their Salary Structure is ignored. Leave a row's “Up to” blank to make it open-ended (& above). If the gross falls in no slab, P-Tax is ₹0."
        />
        <Table rowKey="key" columns={columns} dataSource={rows} loading={loading} size="small" pagination={false}
          locale={{ emptyText: canEdit ? 'No slabs yet — click “Add Row” to create one' : 'No slabs configured' }} />
      </Modal>
    </>
  );
}

// ── Tab 2: payroll runs + payslips ──
function RunsTab({ branches }) {
  const { can } = usePermissions();
  const canCreate = can('hrPayroll', 'create');
  const canEdit = can('hrPayroll', 'edit');

  const [branchId, setBranchId] = useState(null);
  const [period, setPeriod] = useState(dayjs());
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [viewRun, setViewRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cycleInfo, setCycleInfo] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  // The active cycle, plus the days the month in the picker resolves to — so
  // what a run would cover is visible before hitting Generate. Resolved on the
  // server against the saved cycle, for whatever month is picked.
  const loadCycle = async () => {
    try {
      const res = await fetchPayrollCycle();
      setCycleInfo(res?.data?.data || null);
    } catch { /* banner is advisory only */ }
  };

  const loadSelectedPeriod = async () => {
    try {
      const res = await previewPayrollCycle(null, { year: period.year(), month: period.month() + 1, count: 1 });
      setSelectedPeriod(res?.data?.data?.preview?.[0] || null);
    } catch { setSelectedPeriod(null); }
  };

  const loadRuns = async () => {
    setLoading(true);
    try {
      const res = await fetchPayrollRuns({ limit: 100 });
      setRuns(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load runs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRuns(); loadCycle(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSelectedPeriod(); }, [period, cycleInfo]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await generatePayrollRun({ year: period.year(), month: period.month() + 1, ...(branchId ? { branchId } : {}) });
      const { payslips = [], skipped = [] } = res?.data?.data || {};
      message.success(res?.data?.message || `Generated ${payslips.length} payslip(s)`);
      if (skipped.length) {
        Modal.info({
          title: `${skipped.length} employee(s) skipped`,
          content: (
            <ul style={{ paddingLeft: 18 }}>
              {skipped.map((s) => <li key={s.employeeId}>{s.name}: {s.reason}</li>)}
            </ul>
          ),
        });
      }
      loadRuns();
    } catch (err) { message.error(err?.response?.data?.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const openRun = async (run) => {
    setViewRun(run);
    setDetailLoading(true);
    try {
      const res = await fetchPayrollRun(run._id);
      setRunDetail(res?.data?.data || null);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load run'); }
    finally { setDetailLoading(false); }
  };

  const doAction = async (fn, id, okMsg) => {
    try {
      await fn(id);
      message.success(okMsg);
      loadRuns();
      if (viewRun?._id === id) openRun({ _id: id });
    } catch (err) { message.error(err?.response?.data?.message || 'Action failed'); }
  };

  // Archive every payslip PDF into its employee's folder. Failures are reported
  // per employee rather than failing the batch, so the rest still get archived.
  const publishRun = async (run) => {
    setPublishingId(run._id);
    try {
      const res = await publishPayrollRun(run._id);
      const d = res?.data?.data || {};
      message.success(res?.data?.message || 'Payslips archived');
      const failures = (d.results || []).filter((r) => r.outcome === 'FAILED');
      if (failures.length) {
        Modal.warning({
          title: `${failures.length} payslip(s) could not be archived`,
          width: 560,
          content: (
            <ul style={{ paddingLeft: 18 }}>
              {failures.map((f) => <li key={f.payslipId}>{f.employeeName}: {f.message}</li>)}
            </ul>
          ),
        });
      }
      loadRuns();
      if (viewRun?._id === run._id) openRun({ _id: run._id });
    } catch (err) { message.error(err?.response?.data?.message || 'Publish failed'); }
    finally { setPublishingId(null); }
  };

  // Write the file. `run` supplies the name, `slips` the rows — so the same
  // code serves the open modal and a row on the list.
  const writeRunCsv = (run, slips, variant) => {
    if (!slips.length) { message.warning('This run has no payslips to export'); return; }
    const full = variant === 'full';
    const csv = toCsv(slips, full ? FULL_COLUMNS : SUMMARY_COLUMNS);
    const period = dayjs(new Date(run.year, run.month - 1, 1)).format('MMM-YYYY');
    downloadCsv(`payroll-${run.reference}-${period}-${full ? 'full' : 'summary'}.csv`, csv);
    message.success(`Exported ${slips.length} payslip(s)`);
  };

  // From inside the detail modal: the payslips are already loaded, so the file
  // matches what is on screen exactly — a cancelled run's void slips included.
  const exportRun = (variant) => writeRunCsv(runDetail, runDetail?.payslips || [], variant);

  // From a row on the list: the list carries only run totals, so fetch the
  // run's payslips first. Saves opening the run just to export it.
  const exportRunById = async (run, variant) => {
    setExportingId(run._id);
    try {
      const res = await fetchPayrollRun(run._id);
      const detail = res?.data?.data;
      if (!detail) throw new Error('Could not load this run');
      writeRunCsv(detail, detail.payslips || [], variant);
    } catch (err) { message.error(err?.response?.data?.message || err.message || 'Export failed'); }
    finally { setExportingId(null); }
  };

  const retryPayslip = async (slip) => {
    setRetryingId(slip._id);
    try {
      await publishPayslip(slip._id, true);
      message.success(`Archived ${slip.employeeName}'s payslip`);
      if (viewRun?._id) openRun({ _id: viewRun._id });
    } catch (err) { message.error(err?.response?.data?.message || 'Could not archive this payslip'); }
    finally { setRetryingId(null); }
  };

  // Widths are explicit because the table scrolls horizontally — antd only
  // honours a scroll width when the columns it is summing are themselves sized.
  const runColumns = [
    { title: 'Reference', dataIndex: 'reference', width: 120, fixed: 'left', render: (v) => <Text code>{v}</Text> },
    { title: 'Period', width: 100, render: (_, r) => periodLabel(r) },
    { title: 'Covers', width: 190, render: (_, r) => <Text type="secondary" style={{ fontSize: 12 }}>{periodRange(r)}</Text> },
    { title: 'Branch', dataIndex: ['branch', 'name'], width: 150, render: (v) => v || 'All branches' },
    { title: 'Employees', dataIndex: 'employeeCount', align: 'right', width: 100 },
    { title: 'Gross', dataIndex: 'grossTotal', align: 'right', width: 130, render: money },
    { title: 'Net', dataIndex: 'netTotal', align: 'right', width: 130, render: (v) => <Text strong>{money(v)}</Text> },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={RUN_STATUS_COLOR[v]}>{v}</Tag> },
    {
      // Up to four buttons on a FINALIZED run — pinned right so they stay
      // reachable without scrolling back.
      title: 'Actions', width: 390, fixed: 'right', render: (_, r) => (
        <Space>
          <Button size="small" icon={<FileTextOutlined />} onClick={() => openRun(r)}>View</Button>
          <Tooltip title="Export this run to CSV">
            <Dropdown trigger={['click']} menu={{
              items: [
                { key: 'summary', label: 'Ref / Employee / Net Pay' },
                { key: 'full', label: 'Full details' },
              ],
              onClick: ({ key }) => exportRunById(r, key),
            }}>
              <Button size="small" icon={<DownloadOutlined />} loading={exportingId === r._id} />
            </Dropdown>
          </Tooltip>
          {canEdit && r.status === 'DRAFT' && (
            <Popconfirm title="Finalize this run? Payslips will be locked." onConfirm={() => doAction(finalizePayrollRun, r._id, 'Run finalized')}>
              <Button size="small" type="primary">Finalize</Button>
            </Popconfirm>
          )}
          {canEdit && (r.status === 'FINALIZED' || r.status === 'PAID') && (
            <Popconfirm
              title="Archive payslips to employee folders?"
              description="Each payslip PDF is saved to that employee's Payslips folder and becomes visible on their profile."
              onConfirm={() => publishRun(r)}>
              <Button size="small" icon={<CloudUploadOutlined />} loading={publishingId === r._id}>
                {r.publishedAt ? 'Re-archive' : 'Archive PDFs'}
              </Button>
            </Popconfirm>
          )}
          {canEdit && r.status === 'FINALIZED' && (
            <Popconfirm title="Mark all salaries as paid?" onConfirm={() => doAction(markPayrollRunPaid, r._id, 'Marked as paid')}>
              <Button size="small" icon={<CheckOutlined />}>Mark Paid</Button>
            </Popconfirm>
          )}
          {canEdit && r.status !== 'PAID' && r.status !== 'CANCELLED' && (
            <Popconfirm
              title="Cancel this run?"
              description="Its payslips are kept as void records for audit and stay visible here. Employees never see them."
              onConfirm={() => doAction(cancelPayrollRun, r._id, 'Run cancelled')}>
              <Button size="small" danger>Cancel</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const slipColumns = [
    { title: 'Ref', dataIndex: 'reference', width: 120, fixed: 'left', render: (v) => <Text code>{v}</Text> },
    { title: 'Employee', dataIndex: 'employeeName', width: 160 },
    { title: 'Code', dataIndex: 'employeeCode', width: 90 },
    {
      // "30 / 30" — days paid over the divisor they were prorated against.
      title: 'Payable Days', align: 'right', width: 110,
      render: (_, r) => (
        <>
          {r.attendance?.payableDays ?? 0}
          {r.attendance?.prorationDays ? <Text type="secondary"> / {r.attendance.prorationDays}</Text> : null}
        </>
      ),
    },
    { title: 'LWP', dataIndex: ['attendance', 'lwp'], align: 'right', width: 70, render: (v) => v ? <Tag color="red">{v}</Tag> : 0 },
    { title: 'Incentive', dataIndex: ['earnings', 'incentive'], align: 'right', width: 110, render: (v) => v ? <Text style={{ color: '#1a7f37' }}>{money(v)}</Text> : '—' },
    { title: 'Gross', dataIndex: 'grossEarnings', align: 'right', width: 120, render: money },
    { title: 'Deductions', dataIndex: 'totalDeductions', align: 'right', width: 120, render: money },
    { title: 'Net Pay', dataIndex: 'netPay', align: 'right', width: 120, render: (v) => <Text strong>{money(v)}</Text> },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: (v) => <Tag color={SLIP_STATUS_COLOR[v]}>{v}</Tag>,
    },
    {
      // The archived copy in the employee's folder, as opposed to the live PDF
      // the button below re-renders on demand.
      title: 'Archived', width: 150,
      render: (_, r) => {
        if (r.status === 'CANCELLED') return <Text type="secondary">—</Text>;
        const tag = PUBLISH_TAG[r.publishStatus] || PUBLISH_TAG.NOT_PUBLISHED;
        return (
          <Space size={4}>
            {r.pdfUrl
              ? <a href={r.pdfUrl} target="_blank" rel="noreferrer"><Tag color={tag.color}>{tag.label}</Tag></a>
              : <Tag color={tag.color} title={r.publishError || ''}>{tag.label}</Tag>}
            {canEdit && r.publishStatus === 'FAILED' && (
              <Button size="small" type="link" loading={retryingId === r._id} onClick={() => retryPayslip(r)}>Retry</Button>
            )}
          </Space>
        );
      },
    },
    { title: '', width: 90, fixed: 'right', render: (_, r) => <Button size="small" type="link" icon={<DownloadOutlined />} onClick={() => openPayslipPdf(r._id)}>PDF</Button> },
  ];

  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Generate a payroll run to compute prorated payslips from each employee's salary structure and attendance. Finalize to lock, archive the PDFs to each employee's folder, then mark as paid. Cancelling keeps the payslips as void audit records."
        description={cycleInfo && (
          <Text style={{ fontSize: 12 }}>
            Active cycle: <Text strong>{cycleInfo.description}</Text>
            {selectedPeriod && <> — {periodLabel(selectedPeriod)} payroll covers <Text strong>{periodRange(selectedPeriod)}</Text> ({selectedPeriod.totalDays} days, prorated over {selectedPeriod.prorationDays}).</>}
          </Text>
        )}
      />
      <Row justify="end" align="middle" style={{ marginBottom: 12 }}>
        <Space>
          <Select allowClear showSearch optionFilterProp="children" style={{ width: 200 }} placeholder="All branches" value={branchId} onChange={(v) => setBranchId(v || null)}>{branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}</Select>
          <DatePicker picker="month" value={period} onChange={(d) => setPeriod(d || dayjs())} />
          <PayrollCycleButton onSaved={() => { loadCycle(); loadRuns(); }} />
          <PTaxSettingsButton />
          {canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={generate} loading={generating}>Generate / Regenerate Run</Button>}
          <Button icon={<ReloadOutlined />} onClick={loadRuns} loading={loading}>Refresh</Button>
        </Space>
      </Row>

      <Table rowKey="_id" columns={runColumns} dataSource={runs} loading={loading} size="small"
        scroll={{ x: 1420 }} tableLayout="fixed"
        pagination={{ pageSize: 20 }} locale={{ emptyText: 'No payroll runs yet' }} />

      <Modal open={!!viewRun} onCancel={() => { setViewRun(null); setRunDetail(null); }} footer={null} width={1000}
        title={runDetail
          ? <>Payroll Run {runDetail.reference} — {periodLabel(runDetail)} <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>({periodRange(runDetail)})</Text></>
          : 'Payroll Run'} destroyOnClose>
        {runDetail && (
          <>
            {runDetail.status === 'CANCELLED' && (
              <Alert type="error" showIcon style={{ marginBottom: 12 }}
                message="This run was cancelled — its payslips are void."
                description="They are kept here for audit only: they are excluded from annual earnings, were never archived to employee folders, and are not visible to the employees."
              />
            )}
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={6}><Card size="small"><Statistic title="Employees" value={runDetail.employeeCount} /></Card></Col>
              <Col span={6}><Card size="small"><Statistic title="Gross" value={runDetail.grossTotal} prefix="₹" precision={2} /></Card></Col>
              <Col span={6}><Card size="small"><Statistic title="Deductions" value={runDetail.deductionTotal} prefix="₹" precision={2} valueStyle={{ color: '#cf1322' }} /></Card></Col>
              <Col span={6}><Card size="small"><Statistic title="Net Payable" value={runDetail.netTotal} prefix="₹" precision={2} valueStyle={{ color: '#0a58ca' }} /></Card></Col>
            </Row>
            <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {(runDetail.payslips || []).length} payslip(s)
              </Text>
              <Space>
                <Button size="small" icon={<DownloadOutlined />} onClick={() => exportRun('summary')}>
                  Export Ref / Employee / Net Pay
                </Button>
                <Button size="small" type="primary" ghost icon={<DownloadOutlined />} onClick={() => exportRun('full')}>
                  Export Full Details
                </Button>
              </Space>
            </Row>
            <Table rowKey="_id" columns={slipColumns} dataSource={runDetail.payslips || []} loading={detailLoading}
              size="small" pagination={{ pageSize: 20 }} scroll={{ x: 1420 }} locale={{ emptyText: 'No payslips' }} />
          </>
        )}
      </Modal>
    </>
  );
}

// ── Tab 3: sales-based incentives (payroll adjustments) ──
// A very small, header-based CSV parser (no extra dependency). Splits on the
// first row as headers, then maps employeeCode/amount/note columns.
function parseIncentiveCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const codeIdx = header.findIndex((h) => h === 'employeecode' || h === 'code');
  const amtIdx = header.findIndex((h) => h === 'amount' || h === 'incentive' || h === 'incentiveamount');
  const noteIdx = header.findIndex((h) => h === 'note' || h === 'remarks');
  if (codeIdx === -1 || amtIdx === -1) throw new Error('CSV must have "employeeCode" and "amount" columns');
  return lines.slice(1).map((line) => {
    const cells = line.split(',');
    return {
      employeeCode: (cells[codeIdx] || '').trim(),
      amount: Number((cells[amtIdx] || '').trim()),
      note: noteIdx > -1 ? (cells[noteIdx] || '').trim() : '',
    };
  });
}

function IncentivesTab({ branches }) {
  const { can } = usePermissions();
  const canEdit = can('hrPayroll', 'edit');

  const [branchId, setBranchId] = useState(null);
  const [period, setPeriod] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [importing, setImporting] = useState(false);

  const ym = () => ({ year: period.year(), month: period.month() + 1 });

  const load = async () => {
    setLoading(true);
    try {
      const [empRes, incRes] = await Promise.all([
        fetchEmployees({ limit: 2000, ...(branchId ? { branchId } : {}) }),
        fetchIncentives({ ...ym(), ...(branchId ? { branchId } : {}) }),
      ]);
      const employees = empRes?.data?.data || empRes?.data || [];
      const incentives = incRes?.data?.data || [];
      const byEmp = new Map(incentives.map((i) => [String(i.employee), i]));
      setRows(employees.map((e) => {
        const inc = byEmp.get(String(e._id));
        return {
          employeeId: e._id,
          employeeCode: e.employeeCode || '',
          name: e.name || '',
          branchName: e.branch?.name || '',
          amount: inc?.amount || 0,
          note: inc?.note || '',
        };
      }));
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const setField = (employeeId, field, value) =>
    setRows((prev) => prev.map((r) => (r.employeeId === employeeId ? { ...r, [field]: value } : r)));

  const saveRow = async (row) => {
    setSavingId(row.employeeId);
    try {
      await saveIncentive({ employeeId: row.employeeId, ...ym(), amount: row.amount || 0, note: row.note || '' });
      message.success(`Incentive saved for ${row.name}`);
    } catch (err) { message.error(err?.response?.data?.message || 'Save failed'); }
    finally { setSavingId(null); }
  };

  const downloadTemplate = () => {
    const sample = rows.length ? rows.slice(0, 3) : [{ employeeCode: 'EMP001', amount: 5000, note: 'March sales incentive' }];
    const body = sample.map((r) => `${r.employeeCode},${r.amount || 0},${r.note || ''}`).join('\n');
    const csv = `employeeCode,amount,note\n${body}`;
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `incentive-template-${period.year()}-${period.month() + 1}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleUpload = async (file) => {
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = parseIncentiveCsv(text);
      if (!parsed.length) { message.warning('No data rows found in file'); return false; }
      const res = await importIncentives({ rows: parsed, ...ym(), ...(branchId ? { branchId } : {}) });
      const { imported = 0, removed = 0, failed = 0, errors = [] } = res?.data?.data || {};
      message.success(res?.data?.message || `Imported ${imported}`);
      if (failed) {
        Modal.warning({
          title: `${failed} row(s) could not be imported`,
          content: (<ul style={{ paddingLeft: 18 }}>{errors.map((e, i) => <li key={i}>Line {e.line}{e.employeeCode ? ` (${e.employeeCode})` : ''}: {e.reason}</li>)}</ul>),
          width: 520,
        });
      }
      await load();
    } catch (err) { message.error(err?.message || err?.response?.data?.message || 'Import failed'); }
    finally { setImporting(false); }
    return false; // prevent antd Upload's default POST
  };

  const columns = [
    { title: 'Code', dataIndex: 'employeeCode', width: 110, render: (v) => v ? <Text code>{v}</Text> : '—' },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Branch', dataIndex: 'branchName', render: (v) => v || '—' },
    {
      title: 'Incentive (₹)', width: 160, align: 'right',
      render: (_, r) => (
        <InputNumber min={0} style={{ width: 140 }} value={r.amount} disabled={!canEdit}
          formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(v) => (v || '').replace(/,/g, '')}
          onChange={(v) => setField(r.employeeId, 'amount', v || 0)} />
      ),
    },
    {
      title: 'Note', width: 240,
      render: (_, r) => (
        <Input placeholder="Optional" value={r.note} disabled={!canEdit} maxLength={200}
          onChange={(e) => setField(r.employeeId, 'note', e.target.value)} />
      ),
    },
    ...(canEdit ? [{
      title: '', width: 90,
      render: (_, r) => (
        <Button size="small" type="primary" icon={<SaveOutlined />} loading={savingId === r.employeeId} onClick={() => saveRow(r)}>Save</Button>
      ),
    }] : []),
  ];

  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);

  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Enter each employee's sales-based incentive for the month, or import a CSV. Incentives are paid in full (not prorated) and can be edited until that month's payroll run is finalized. Generate/regenerate the run afterwards to pull them into payslips."
      />
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Space>
          <Statistic title="Total incentive (shown)" value={total} prefix="₹" precision={2} valueStyle={{ fontSize: 16 }} />
        </Space>
        <Space wrap>
          <Select allowClear showSearch optionFilterProp="children" style={{ width: 200 }} placeholder="All branches" value={branchId} onChange={(v) => setBranchId(v || null)}>{branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}</Select>
          <DatePicker picker="month" value={period} onChange={(d) => setPeriod(d || dayjs())} />
          <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>Load</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>Template</Button>
          {canEdit && (
            <Upload accept=".csv" showUploadList={false} beforeUpload={handleUpload}>
              <Button icon={<UploadOutlined />} loading={importing}>Import CSV</Button>
            </Upload>
          )}
        </Space>
      </Row>
      <Table rowKey="employeeId" columns={columns} dataSource={rows} loading={loading} size="small" scroll={{ x: 900 }}
        pagination={{ pageSize: 50, showTotal: (t) => `${t} employees` }}
        locale={{ emptyText: 'Load a month to enter incentives' }} />
    </>
  );
}

export default function PayrollPreparation() {
  const [branches, setBranches] = useState([]);

  useEffect(() => { fetchBranches({ limit: 1000 }).then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {}); }, []);

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={5} style={{ margin: '0 0 12px' }}><DollarOutlined style={{ marginRight: 8 }} />Payroll</Title>
      <Tabs
        defaultActiveKey="runs"
        items={[
          { key: 'runs', label: 'Payroll Runs & Payslips', children: <RunsTab branches={branches} /> },
          { key: 'incentives', label: <span><GiftOutlined style={{ marginRight: 6 }} />Incentives</span>, children: <IncentivesTab branches={branches} /> },
          { key: 'prep', label: 'Attendance Preparation', children: <PreparationTab branches={branches} /> },
        ]}
      />
    </div>
  );
}
